import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * MobileStickyCTA
 * - Hidden until the user has scrolled past 50% of the page
 * - Hides itself again when the page's final CTA section enters the viewport
 *   (so the user never sees two booking buttons at once)
 * - Smooth fade + slide animation
 * - Mobile only (md:hidden)
 *
 * @param {Object}   props
 * @param {Object}   props.hideWhenInView  React ref pointing to a section
 *                                         (typically the final CTA). When that
 *                                         section is intersecting the viewport,
 *                                         the sticky CTA fades out.
 */
const MobileStickyCTA = ({ hideWhenInView }) => {
  const [pastHalf, setPastHalf] = useState(false);
  const [hideForFooter, setHideForFooter] = useState(false);

  // Show after 50% scroll progress.
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const docH = Math.max(0, doc.scrollHeight - window.innerHeight);
      const pct = docH > 0 ? window.scrollY / docH : 0;
      setPastHalf(pct >= 0.5);
    };
    onScroll(); // run once for refresh / deep links
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hide when the final CTA (or footer area) is on screen — avoids duplicate buttons.
  useEffect(() => {
    const node = hideWhenInView?.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHideForFooter(entry.isIntersecting),
      { threshold: 0.15 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hideWhenInView]);

  const visible = pastHalf && !hideForFooter;

  return (
    <div
      data-testid="mobile-sticky-cta"
      aria-hidden={!visible}
      className={`md:hidden fixed bottom-0 inset-x-0 z-40 px-4 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] bg-[#FAFAF7]/92 backdrop-blur supports-[backdrop-filter]:bg-[#FAFAF7]/85 border-t border-[#EDE8DC] transition-[transform,opacity] duration-300 ease-out ${
        visible
          ? 'translate-y-0 opacity-100 pointer-events-auto'
          : 'translate-y-full opacity-0 pointer-events-none'
      }`}
    >
      <Link to="/booking" className="block">
        <Button
          data-testid="mobile-sticky-cta-btn"
          className="w-full h-11 bg-[#5D4E37] hover:bg-[#6B5D4F] text-white text-[13.5px] font-light tracking-wide rounded-sm gap-2 shadow-[0_8px_22px_-12px_rgba(93,78,55,0.45)]"
        >
          Commencer la guitare
          <ArrowRight className="w-4 h-4" />
        </Button>
      </Link>
    </div>
  );
};

export default MobileStickyCTA;
